/**
 * Datenschutzerklärung – Novatus Sales Academy.
 *
 * Hinweis: Dieser Text ist ein sorgfältig erstelltes Template für die konkrete
 * technische Realität der App (Hetzner S3 / OpenAI / Stripe / Emergent / SMTP).
 * Vor produktiver Nutzung sollte er anwaltlich oder per eRecht24 geprüft werden,
 * da rechtliche Erfordernisse sich ändern können und individuelle Konstellationen
 * (z. B. konkrete Datenkategorien Eurer Endkunden) ergänzungspflichtig sein können.
 */
import LegalLayout from "../components/LegalLayout";

const DatenschutzPage = () => (
  <LegalLayout
    title="Datenschutzerklärung"
    subtitle="Informationen zur Verarbeitung personenbezogener Daten nach Art. 13 / 14 DSGVO"
    data-testid="datenschutz-page"
  >
    <section>
      <h2>1. Verantwortlicher</h2>
      <p>
        Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) ist:
      </p>
      <p>
        <strong>NOVATUS Energie GmbH</strong>
        <br />
        Maximiliankorso 60
        <br />
        13465 Berlin, Deutschland
        <br />
        Telefon: <a href="tel:+493039820570">+49 (0)30 398205700</a>
        <br />
        E-Mail: <a href="mailto:info@novatus-se.com">info@novatus-se.com</a>
        <br />
        Geschäftsführer: Dipl.-Verw. Wiss. Christof Schirm
      </p>
    </section>

    <section>
      <h2>2. Datenschutzbeauftragter</h2>
      <p>
        Ein Datenschutzbeauftragter ist derzeit nicht bestellt, da die gesetzlichen
        Schwellenwerte nach § 38 BDSG nicht erreicht werden. Bei Fragen zum Datenschutz
        wenden Sie sich bitte direkt an{" "}
        <a href="mailto:info@novatus-se.com">info@novatus-se.com</a>.
      </p>
    </section>

    <section>
      <h2>3. Allgemeines zur Datenverarbeitung</h2>
      <p>
        Die <strong>Novatus Sales Academy</strong> (im Folgenden „Plattform") ist ein KI-gestütztes
        Trainings-Tool für Vertriebsteams im Energiesektor. Wir verarbeiten personenbezogene Daten
        ausschließlich, soweit dies zur Bereitstellung der Plattform und ihrer Funktionalitäten
        sowie zur Erfüllung gesetzlicher Pflichten erforderlich ist. Rechtsgrundlagen sind
        insbesondere Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung), Art. 6 Abs. 1 lit. c DSGVO
        (rechtliche Verpflichtung) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse).
      </p>
    </section>

    <section>
      <h2>4. Kategorien verarbeiteter Daten</h2>
      <ul>
        <li>
          <strong>Stammdaten:</strong> Vor- und Nachname, E-Mail-Adresse, Telefonnummer (für OTP-Login),
          Unternehmenszugehörigkeit, Rolle (Verkäufer / Direktor / Trainer).
        </li>
        <li>
          <strong>Authentifizierungsdaten:</strong> einmalige OTP-Codes (Einmalpasswörter), JWT-Session-Token.
        </li>
        <li>
          <strong>Trainingsdaten:</strong> Gesprächsverläufe (Text + Voice), Transkripte (Whisper),
          Sprach-Analytics (Sprechgeschwindigkeit, Pausen, Füllwörter), KI-Coaching-Feedback, Score- und
          XP-Verlauf.
        </li>
        <li>
          <strong>Audio-Aufzeichnungen:</strong> Bei Voice-Trainings werden die gesprochenen
          Verkäufer-Beiträge zwischengespeichert (siehe Abschnitt 7).
        </li>
        <li>
          <strong>Zahlungsdaten:</strong> für Käufe und Abonnements ausschließlich beim
          Zahlungsabwickler Stripe; wir erhalten lediglich Bestätigungs-Metadaten.
        </li>
        <li>
          <strong>Technische Daten:</strong> IP-Adresse, Browser-/Geräteinformationen, Zeitstempel,
          Audit-Logs (z. B. wer hat welche Aufzeichnung wann angehört).
        </li>
      </ul>
    </section>

    <section>
      <h2>5. Registrierung und Login (OTP-Verfahren)</h2>
      <p>
        Die Anmeldung erfolgt passwortlos per Einmalcode (OTP), der an die hinterlegte E-Mail-Adresse
        oder Telefonnummer gesendet wird. Der OTP-Code ist 10 Minuten gültig und wird nach Verbrauch
        bzw. Ablauf automatisch gelöscht. Versand erfolgt über unseren E-Mail-Provider
        united-domains AG (Eu/DE).
      </p>
      <p>
        Nach erfolgreicher Verifikation wird ein zeitlich begrenztes Sitzungs-Token (JWT) erstellt
        und im Browser gespeichert. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
      </p>
    </section>

    <section>
      <h2>6. KI-gestützte Sprach- und Textverarbeitung</h2>
      <p>
        Zur Bereitstellung der KI-Rollenspiele und des Coachings nutzen wir Modelle von{" "}
        <strong>OpenAI, Inc.</strong> (USA) sowie über die Emergent-Plattform vermittelte Sprachmodelle:
      </p>
      <ul>
        <li>
          <strong>OpenAI Realtime API / GPT-4o-mini</strong>: Generierung der KI-Antworten in
          Trainings-Dialogen. Inhalte werden in Echtzeit übertragen, von OpenAI laut deren
          aktuellen API-Bedingungen <em>nicht zum Trainieren der Modelle verwendet</em> und nicht
          länger als 30 Tage zwecks Missbrauchsprävention zwischengespeichert.
        </li>
        <li>
          <strong>OpenAI Whisper</strong>: Transkription von Sprach-Aufnahmen in Text. Audio-Daten
          werden ausschließlich zur Transkription verarbeitet und nicht dauerhaft bei OpenAI gespeichert.
        </li>
      </ul>
      <p>
        Mit OpenAI wurde ein Auftragsverarbeitungsvertrag (AVV) inkl. EU-Standardvertragsklauseln
        nach Art. 46 DSGVO abgeschlossen. Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO.
      </p>
    </section>

    <section>
      <h2>7. Aufzeichnung von Voice-Trainings</h2>
      <p>
        Bei aktivierten Sprach-Trainings <strong>(passive_call / active_call)</strong> werden die
        Beiträge der trainierenden Person als Audio-Mitschnitt zur späteren Trainings-Reflexion
        gespeichert. KI-generierte Antworten der simulierten Kunden werden <em>nicht</em> als Audio
        gespeichert (nur als Text-Transkript).
      </p>
      <ul>
        <li>
          <strong>Speicherort:</strong> Hetzner Object Storage, Standort Falkenstein,
          Deutschland (Hetzner Online GmbH).
        </li>
        <li>
          <strong>Verschlüsselung:</strong> Transportverschlüsselung TLS 1.2+ sowie
          Ruhe-Verschlüsselung server-seitig im Object Storage.
        </li>
        <li>
          <strong>Speicherdauer:</strong> automatische Löschung nach 90 Tagen via S3-Lifecycle-Policy.
          Eine vorzeitige Löschung kann jederzeit gefordert werden (siehe Abschnitt 13).
        </li>
        <li>
          <strong>Zugriff:</strong> Die trainierende Person selbst sowie ausschließlich autorisierte
          Trainer/Manager der eigenen Organisation. Jeder Zugriff wird in einem Audit-Log
          protokolliert.
        </li>
        <li>
          <strong>Hinweis vor Aufnahme:</strong> Vor jedem Voice-Call wird ein klar sichtbarer
          Datenschutzhinweis angezeigt.
        </li>
      </ul>
      <p>
        Mit der Hetzner Online GmbH wurde ein Auftragsverarbeitungsvertrag (AVV) abgeschlossen.
        Rechtsgrundlage: Art. 6 Abs. 1 lit. b DSGVO (Vertragserfüllung Trainings-Dienstleistung).
      </p>
    </section>

    <section>
      <h2>8. Zahlungsabwicklung (Stripe)</h2>
      <p>
        Für den Kauf von Minuten-Paketen und Abonnements nutzen wir <strong>Stripe Payments Europe, Ltd.</strong>{" "}
        (Irland, DSGVO-konformer Sitz innerhalb der EU). Zahlungsdaten (Kreditkartennummer,
        Bankverbindung) werden ausschließlich von Stripe verarbeitet, wir erhalten nur
        Bestätigungs-Metadaten (Transaktion erfolgreich, gewählter Plan, Rechnungsdaten zur
        Buchhaltung).
      </p>
      <p>
        Mit Stripe wurde ein Auftragsverarbeitungsvertrag abgeschlossen. Datenschutz-Hinweise von
        Stripe:{" "}
        <a href="https://stripe.com/de/privacy" target="_blank" rel="noreferrer">
          https://stripe.com/de/privacy
        </a>
        .
      </p>
    </section>

    <section>
      <h2>9. Hosting und technische Bereitstellung</h2>
      <p>
        Die Anwendung wird auf der Plattform <strong>Emergent</strong> (Emergent Labs, USA / EU-Region)
        gehostet. Datenbank-Inhalte (MongoDB) werden in der EU-Region gespeichert. Mit dem
        Plattformbetreiber wurde ein Auftragsverarbeitungsvertrag abgeschlossen.
      </p>
    </section>

    <section>
      <h2>10. E-Mail-Versand</h2>
      <p>
        Transaktionale E-Mails (Login-Codes, Bestätigungen) werden über die <strong>united-domains AG</strong>{" "}
        (Deutschland) versendet. Mit united-domains besteht ein Vertrag zur Datenverarbeitung. Es
        werden ausschließlich die zum Versand erforderlichen Daten übergeben (E-Mail-Adresse, Code,
        Zeitstempel).
      </p>
    </section>

    <section>
      <h2>11. Newsletter / Marketing-E-Mails (Double-Opt-In)</h2>
      <p>
        Sofern Sie unseren Newsletter abonnieren oder anderweitig der Zusendung von Marketing-Informationen
        zustimmen, verarbeiten wir Ihre E-Mail-Adresse, Ihren Namen sowie den Zeitstempel der Zustimmung
        zum Zweck regelmäßiger Informationen über die Novatus Sales Academy, neue Funktionen, Termine und
        Angebote.
      </p>
      <p>
        Wir verwenden hierfür das <strong>Double-Opt-In-Verfahren</strong>: Nach Anmeldung erhalten Sie
        eine Bestätigungs-E-Mail mit einem Link, der zur Aktivierung Ihres Abonnements geklickt werden muss.
        Erst nach dieser Bestätigung erhalten Sie tatsächlich Mailings.
      </p>
      <p>
        <strong>Rechtsgrundlage:</strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung). Sie können Ihre
        Einwilligung jederzeit mit Wirkung für die Zukunft widerrufen — entweder per Klick auf den
        Abmelde-Link am Ende jeder Marketing-Mail oder per E-Mail an{" "}
        <a href="mailto:info@novatus-se.com">info@novatus-se.com</a>. Die Rechtmäßigkeit der bis zum
        Widerruf erfolgten Verarbeitung bleibt davon unberührt.
      </p>
      <p>
        <strong>Speicherdauer:</strong> Ihre E-Mail-Adresse wird so lange gespeichert, bis Sie den
        Newsletter abbestellen, danach maximal 3 Jahre in einer Sperrliste, um eine erneute ungewollte
        Anmeldung zu verhindern (berechtigtes Interesse, Art. 6 Abs. 1 lit. f DSGVO).
      </p>
    </section>

    <section>
      <h2>12. Cookies und Local Storage</h2>
      <p>
        Die Plattform setzt ausschließlich <strong>technisch notwendige</strong> Elemente im Local
        Storage Ihres Browsers ein (insbesondere das Session-Token zur Aufrechterhaltung des Logins).
        Eine Einwilligung nach § 25 Abs. 1 TDDDG ist hierfür nicht erforderlich, da diese Speicherung
        zur Bereitstellung des ausdrücklich gewünschten Dienstes unbedingt erforderlich ist
        (§ 25 Abs. 2 Nr. 2 TDDDG).
      </p>
      <p>
        Tracking-Cookies, Marketing-Cookies oder Cookies von Drittanbietern (z. B. Werbenetzwerke)
        setzen wir <strong>nicht</strong> ein.
      </p>
    </section>

    <section>
      <h2>13. Ihre Rechte als betroffene Person</h2>
      <p>Ihnen stehen die folgenden Rechte zu:</p>
      <ul>
        <li><strong>Auskunft</strong> (Art. 15 DSGVO) über die zu Ihrer Person gespeicherten Daten</li>
        <li><strong>Berichtigung</strong> (Art. 16 DSGVO) unrichtiger Daten</li>
        <li>
          <strong>Löschung</strong> (Art. 17 DSGVO) – inkl. der vollständigen Entfernung aller Audio-Aufzeichnungen
          aus dem Hetzner Object Storage durch unseren Admin
        </li>
        <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
        <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO) – Export Ihrer Trainings-Historie als JSON</li>
        <li><strong>Widerspruch</strong> gegen Verarbeitungen, die auf Art. 6 Abs. 1 lit. f DSGVO gestützt sind (Art. 21 DSGVO)</li>
        <li>
          <strong>Widerruf von Einwilligungen</strong> (Art. 7 Abs. 3 DSGVO), wirkt nur für die Zukunft
        </li>
        <li>
          <strong>Beschwerde bei einer Aufsichtsbehörde</strong> (Art. 77 DSGVO) – zuständig ist für uns die
          Berliner Beauftragte für Datenschutz und Informationsfreiheit, Friedrichstraße 219, 10969 Berlin.
        </li>
      </ul>
      <p>
        Zur Ausübung Ihrer Rechte wenden Sie sich bitte formlos an{" "}
        <a href="mailto:info@novatus-se.com">info@novatus-se.com</a>. Wir werden Ihrem Anliegen innerhalb
        eines Monats nachkommen.
      </p>
    </section>

    <section>
      <h2>14. Speicherdauer</h2>
      <ul>
        <li>Stammdaten: bis zum Widerruf bzw. zur Beendigung des Vertragsverhältnisses</li>
        <li>Trainingsdaten / Transkripte / KI-Feedback: maximal 24 Monate ab letzter Aktivität</li>
        <li>Audio-Aufzeichnungen: 90 Tage (automatische Löschung)</li>
        <li>Zahlungsbelege / Rechnungen: 10 Jahre (handels- und steuerrechtliche Aufbewahrungspflicht)</li>
        <li>Audit-Logs (Zugriffe auf Aufzeichnungen): 12 Monate</li>
        <li>OTP-Codes: max. 10 Minuten</li>
      </ul>
    </section>

    <section>
      <h2>15. Datensicherheit</h2>
      <p>
        Wir setzen aktuelle Sicherheitsmaßnahmen ein, insbesondere TLS-Verschlüsselung zwischen
        Browser und Server, server-seitige Verschlüsselung im Object Storage, signierte und zeitlich
        begrenzte Zugriffs-URLs für Audio-Wiedergabe, rollenbasierte Zugriffsrechte sowie ein
        Audit-Log für sensible Operationen. Unsere Sicherheitsmaßnahmen werden regelmäßig
        überprüft und an den Stand der Technik angepasst.
      </p>
    </section>

    <section>
      <h2>16. Änderungen dieser Datenschutzerklärung</h2>
      <p>
        Wir behalten uns vor, diese Datenschutzerklärung anzupassen, sofern sich gesetzliche
        Vorgaben oder die tatsächlich genutzten Technologien ändern. Die jeweils aktuelle Version
        finden Sie stets unter <code>/datenschutz</code>.
      </p>
    </section>

    <p className="text-xs text-novatus-text-3 mt-8">
      Stand: {new Date().toLocaleDateString("de-DE", { year: "numeric", month: "long" })}
    </p>
  </LegalLayout>
);

export default DatenschutzPage;
